import { Component, computed, inject, OnInit, Signal, signal, TemplateRef, viewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, take } from 'rxjs/operators';

import { Router, RouterOutlet } from '@angular/router';
import { Actions, ofActionDispatched, select, Store } from '@ngxs/store';
import * as Highcharts from 'highcharts';
import { de_DE, NzI18nService, zh_CN } from 'ng-zorro-antd/i18n';
import { Observable } from 'rxjs';

import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';

import { CommonModule } from '@angular/common';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzContentComponent, NzHeaderComponent, NzLayoutComponent } from 'ng-zorro-antd/layout';
import { languages, params } from './configurations';
import { SignupAPI } from './core/api/signup.service';
import { AccountMoveBannerComponent } from './core/components/account-move-banner/account-move-banner.component';
import { EmailVerificationComponent } from './core/components/email-verification/email-verification.component';
import { SubscriptionAlertComponent } from './core/components/subscription-alert/subscription-alert.component';
import { Toast } from './core/models/common.models';
import { User, UserLanguage } from './core/models/user.models';
import { LoggingService } from './core/services/error-logging.service';
import { GoogleTagManagerService } from './core/services/google-tag-manager.service';
import { StateService } from './core/services/state.service';
import {
  AppHasAuth,
  IsBilledApp,
  IsOauthApp,
  IsPortalApp,
  IsSignupApp,
  RemoveLoader,
} from './core/services/utilities.service';
import { GetAllAccounts } from './core/store/states/accounts/accounts-state.actions';
import { AccountsState } from './core/store/states/accounts/accounts.state';
import { CustomerState } from './core/store/states/customer/customer.state';
import { DisplayToast } from './core/store/states/ui/ui-state.actions';
import { UserState } from './core/store/states/user/user.state';
import { hasValidBillingKey } from './core/utilities/url.utilities';
import { GlobalSideNavComponent, HeaderComponent } from './shared/components';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';
import { SetHighChartsLocaleOptions } from './shared/modules/charts/chart.utilities';
import { DisallowedOptimizationModule } from './shared/modules/disallowed-optimization/disallowed-optimization.module';
import { shareRef } from './shared/operators/share-ref.operator';
import { SharedDirectivesModule } from './shared/shared-directives.module';
import { SharedModule } from './shared/shared.module';

/**
 * App Component
 * Top Level Component
 */
@Component({
  selector: 'adspt-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    NzLayoutComponent,
    NzHeaderComponent,
    HeaderComponent,
    GlobalSideNavComponent,
    SharedModule,
    NzContentComponent,
    NzAlertComponent,
    AccountMoveBannerComponent,
    SubscriptionAlertComponent,
    EmailVerificationComponent,
    ImpersonationBannerComponent,
    DisallowedOptimizationModule,
    RouterOutlet,
    SharedDirectivesModule,
    CommonModule,
    TranslatePipe,
  ],
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private translate = inject(TranslateService);
  private i18n = inject(NzI18nService);
  private loggingService = inject(LoggingService);
  private ss = inject(StateService);
  private signupAPI = inject(SignupAPI);
  private store = inject(Store);
  private actions = inject(Actions);
  private gtm = inject(GoogleTagManagerService);

  readonly appHasAuth = AppHasAuth();
  readonly isOauthApp = IsOauthApp();
  readonly isBilledApp = IsBilledApp();
  readonly isSignupApp = IsSignupApp();
  readonly isPortalApp = IsPortalApp();

  readonly processing = signal(true);
  readonly initializeRoutes = signal(false);
  readonly errTemplate = signal<TemplateRef<void> | null>(null);
  readonly errGenericTmpl = viewChild<TemplateRef<void>>('errGeneric');
  readonly inValidUserTmpl = viewChild<TemplateRef<void>>('errInValidUser');

  readonly user = select(UserState.user);
  readonly customer = select(CustomerState.customer);
  readonly state = select(RouterNavState.queryParams);
  readonly customerId = select(CustomerState.customerId);
  readonly customerSummary = select(CustomerState.summary);
  readonly hasAccounts = select(AccountsState.hasAccounts);
  readonly language = select(RouterNavState.queryParam('lang'));
  readonly firstSyncPending = select(AccountsState.firstSyncPending);
  readonly isFluidLayout = select(RouterNavState.dataParam('isFluid'));
  readonly isFullWidth = select(RouterNavState.dataParam('fullWidth'));
  readonly isAdminPage = select(RouterNavState.dataParam('isAdminPage'));
  readonly impersonationId = select(RouterNavState.queryParam(params.IMPERSONATION_ID));

  readonly hasErrors = computed(() => !!this.errTemplate());
  readonly defaultLanguage = computed(() => (this.language() || this.translate.getBrowserLang()).toUpperCase());
  readonly isLoaded = computed(() => (this.appHasAuth ? !!this.user() : true));
  readonly showRouterOutlet = computed(() => !(!this.initializeRoutes() || this.hasErrors()));
  readonly disableLinks = computed(() => {
    if (this.appHasAuth) {
      return !this.hasAccounts() || this.firstSyncPending();
    }
    return true;
  });
  readonly monitorUrl = computed(() => {
    let url = ['/monitor?'];

    if (this.customerId()) {
      url.push(`${params.CUSTOMER_ID}=${this.customerId()}&`);
    }

    if (this.impersonationId()) {
      url.push(`${params.IMPERSONATION_ID}=${this.impersonationId()}&`);
    }

    return url.join('').slice(0, -1);
  });
  readonly layoutClass = computed(() =>
    this.isFullWidth() ? 'container-full-width' : this.isFluidLayout() ? 'container-fluid' : 'container',
  );

  @Dispatch() hideToast = () => new DisplayToast({ show: false });

  readonly toast$: Observable<Partial<Toast>> = this.actions.pipe(
    ofActionDispatched(DisplayToast),
    map((action) => action.toast),
    shareRef(),
  );

  /**
   * fetch and load translations file
   */
  private _setTranslations() {
    let current: UserLanguage = (this.isSignupApp ? this.signupAPI.userInfo?.lang : this.user()?.lang)?.toUpperCase();

    if (this.isPortalApp) current = <UserLanguage>this.defaultLanguage().toLowerCase();
    else
      current = <UserLanguage>(Object.values(languages).includes(current) ? current : languages.ENGLISH).toLowerCase();

    this.translate
      .use(current)
      .pipe(take(1))
      .subscribe({
        next: () => {
          setTimeout(() => {
            if (this.appHasAuth && !this.hasErrors()) {
              // set new state for apollo, skipping this for connect&billed app and if there is an error
              const queryParams = { ...this.state(), [params.CUSTOMER_ID]: this.customerId() }; // getRawQueryParams is replace by this.state()
              this.router.navigate([location.pathname], { queryParams, queryParamsHandling: 'merge' });
            }
            this._setAntLocale(current);
            this._setLoaderFlags();
          });
        },
        error: console.error,
      });
  }

  private _setLoaderFlags() {
    this.initializeRoutes.set(this.isLoaded());
    this.processing.set(!this.initializeRoutes());
    if (this.initializeRoutes()) {
      RemoveLoader();
    }
  }

  private _setErrorTemplate(templateSignal: Signal<TemplateRef<void>>) {
    this._setLoaderFlags();
    this.errTemplate.set(templateSignal());
  }

  private _setAntLocale(lang: string) {
    if (!lang) lang = this.defaultLanguage();

    switch (lang.toUpperCase()) {
      case languages.GERMAN:
        this.i18n.setLocale(de_DE);
        break;
      case languages.CHINESE:
        this.i18n.setLocale(zh_CN);
        break;
    }
  }

  private _initBillingApp(): void {
    this._setTranslations();
  }
  private _initPortalApp(): void {
    this._setTranslations();
  }

  private _initSignup(): void {
    let user = <User>{};
    this.signupAPI
      .getUserInfo()
      .pipe(take(1))
      .subscribe({
        next: (res: User) => {
          user = res;
          this.loggingService.config(user);
          this.signupAPI.isCustomerSignup = false;
        },
        error: () => {
          // if user info fails it means user is not auth/ no user recors exists
          // and use customer signup by default
          this.signupAPI.isCustomerSignup = true;
        },
      })
      .add(() => {
        this.signupAPI.userInfo = user;
        this.gtm.init(user);
        this._setTranslations();
      });
  }

  private _init(): void {
    const user = this.user();
    const customer = this.customer();

    if (user === undefined || customer === undefined) {
      this._setErrorTemplate(this.inValidUserTmpl);
    }

    window.adspert_data.user = user;
    window.adspert_data.customer = customer;
    window.adspert_data.customerSummary = this.customerSummary();
    Highcharts.setOptions(SetHighChartsLocaleOptions(user.lang));

    this.store
      .dispatch(new GetAllAccounts(customer.customerId))
      .pipe(take(1))
      .subscribe({
        error: () => this._setErrorTemplate(this.errGenericTmpl),
      })
      .add(() => {
        this._setTranslations();
        this.gtm.init(user, customer);
      });
  }

  /**
   * create listener for state changes
   */
  constructor() {
    this.ss.setState();
  }

  ngOnInit() {
    // intialization and some settings are diffrent for connect and apollo
    if (this.isBilledApp) {
      if (!hasValidBillingKey()) {
        this._initBillingApp();
      }
    } else if (this.isSignupApp) {
      this._initSignup();
    } else if (this.isPortalApp) {
      this._initPortalApp();
    } else {
      this._init();
    }
  }
}
